import { Modal, Button, Space, Tabs } from 'antd';
import { useState, useRef, useEffect } from 'react';
import { BrowserQRCodeReader } from '@zxing/library';
import { TextSmall } from '../../../components/text';
import { openAlert } from '../../../components/alert/hooks/useAlert';
import { FaQrcode, FaCamera, FaImage } from 'react-icons/fa';

export const QRScannerModal = ({ open, onClose, onDetected }) => {
  const [scanResult, setScanResult] = useState(null);
  const [isScanning, setIsScanning] = useState(false);
  const [activeTab, setActiveTab] = useState('camera');
  const videoRef = useRef(null);
  const fileInputRef = useRef(null);
  const codeReaderRef = useRef(null);
  const streamRef = useRef(null);
  const isProcessingRef = useRef(false);

  useEffect(() => {
    if (open) {
      setScanResult('');
      setIsScanning(false);
      setActiveTab('camera');
      isProcessingRef.current = false;
    } else {
      stopCamera();
      isProcessingRef.current = false;
    }
    return () => {
      stopCamera();
      isProcessingRef.current = false;
    };
  }, [open]);

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (codeReaderRef.current) {
      codeReaderRef.current.reset();
      codeReaderRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  const startCameraScan = async () => {
    try {
      setIsScanning(true);
      stopCamera();

      const codeReader = new BrowserQRCodeReader();
      codeReaderRef.current = codeReader;

      const scanCallback = (result, error) => {
        if (result && !isProcessingRef.current) {
          isProcessingRef.current = true;

          const scannedCode = result.getText();
          let validatedCode = scannedCode;
          if (scannedCode.includes('http')) {
            const url = new URL(scannedCode);
            const params = url.searchParams;
            validatedCode = params.get('fw');
          }
          setScanResult(validatedCode);
          setIsScanning(false);
          stopCamera();
          onDetected(validatedCode);
          onClose();
        }
        if (error && error.name !== 'NotFoundException') {
          console.error('Scan error:', error);
        }
      };

      try {
        // Force rear camera (environment) on mobile
        await codeReader.decodeFromConstraints(
          { video: { facingMode: { exact: 'environment' } } },
          videoRef.current,
          scanCallback,
        );
      } catch {
        // Fallback for devices without a rear camera (e.g. desktop)
        await codeReader.decodeFromVideoDevice(undefined, videoRef.current, scanCallback);
      }

      if (videoRef.current && videoRef.current.srcObject) {
        streamRef.current = videoRef.current.srcObject;
      }
    } catch (error) {
      console.error('Camera error:', error);
      openAlert({
        type: 'error',
        message: 'ไม่สามารถเข้าถึงกล้องได้ กรุณาตรวจสอบว่าเบราว์เซอร์ได้รับอนุญาตให้เข้าถึงกล้องหรือไม่',
      });
      setIsScanning(false);
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file || isProcessingRef.current) return;

    try {
      isProcessingRef.current = true;
      setIsScanning(true);
      const codeReader = new BrowserQRCodeReader();

      const reader = new FileReader();
      reader.onload = async () => {
        const img = new Image();
        img.src = reader.result;
        img.onload = async () => {
          try {
            const result = await codeReader.decodeFromImageElement(img);
            const scannedCode = result.getText();
            console.log('scannedCode', scannedCode);
            let validatedCode = scannedCode;
            // validate scannedCode is url or not
            if (scannedCode.includes('http')) {
              // get query params from url
              const url = new URL(scannedCode);
              const params = url.searchParams;
              validatedCode = params.get('fw');
            }
            setScanResult(validatedCode);
            onDetected(validatedCode);
            onClose();
          } catch (error) {
            openAlert({
              type: 'error',
              message: 'ไม่สามารถอ่าน QR code จากภาพนี้ได้ กรุณาลองใหม่อีกครั้ง',
            });
          } finally {
            setIsScanning(false);
            isProcessingRef.current = false;
          }
        };
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Image scan error:', error);
      openAlert({
        type: 'error',
        message: 'เกิดข้อผิดพลาดในการอ่าน QR code',
      });
      setIsScanning(false);
      isProcessingRef.current = false;
    }
  };

  const handleOpenFilePicker = () => {
    fileInputRef.current?.click();
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
    if (key === 'camera') {
      stopCamera();
    }
  };
  const tabItems = [
    {
      key: 'camera',
      label: (
        <Space>
          <FaCamera />
          ใช้กล้อง
        </Space>
      ),
      children: (
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <div
            style={{
              width: '100%',
              maxWidth: '500px',
              height: '100%',
              margin: '0 auto 20px',
              position: 'relative',
              backgroundColor: '#fff',
              borderRadius: '8px',
              overflow: 'hidden',
            }}
          >
            <video
              ref={videoRef}
              style={{
                width: '100%',
                height: 'auto',
                display: 'block',
              }}
              playsInline
            />
            {!isScanning && (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '12px',
                  backgroundColor: 'rgba(216, 216, 216, 0.7)',
                  color: '#000',
                }}
              >
                <FaQrcode style={{ fontSize: 48, color: '#1890ff' }} />
                <TextSmall
                  text="สแกน QR Code รหัสรับประกัน"
                  style={{ color: '#000', fontWeight: 600 }}
                />
                <TextSmall text="กดปุ่มเริ่มสแกนเพื่อเปิดกล้อง" style={{ color: '#666' }} />
              </div>
            )}
          </div>
          <Button
            type="primary"
            size="large"
            icon={<FaQrcode />}
            onClick={startCameraScan}
            loading={isScanning}
            disabled={isScanning}
            style={{ width: '100%', maxWidth: '300px' }}
          >
            {isScanning ? 'กำลังสแกน...' : 'เริ่มสแกน QR Code'}
          </Button>
          {isScanning && (
            <Button
              style={{ marginTop: '10px', width: '100%', maxWidth: '300px' }}
              onClick={() => {
                stopCamera();
                setIsScanning(false);
              }}
            >
              หยุดสแกน
            </Button>
          )}
        </div>
      ),
    },
    {
      key: 'upload',
      label: (
        <Space>
          <FaImage />
          อัปโหลดรูปภาพ
        </Space>
      ),
      children: (
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <TextSmall
            text="เลือกรูปภาพที่มี QR code เพื่อสแกน"
            style={{ marginBottom: '20px', display: 'block' }}
          />
          <input
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            ref={fileInputRef}
            onChange={handleImageUpload}
          />
          <Button
            type="primary"
            size="large"
            icon={<FaImage />}
            onClick={handleOpenFilePicker}
            loading={isScanning}
            style={{ width: '100%', maxWidth: '300px' }}
          >
            เลือกรูปภาพ
          </Button>
        </div>
      ),
    },
  ];

  return (
    <Modal
      title={
        <Space>
          <FaQrcode />
          สแกน QR Code
        </Space>
      }
      open={open}
      onCancel={() => {
        stopCamera();
        onClose();
      }}
      footer={null}
      width={600}
      style={{ top: '5%' }}
    >
      <Tabs activeKey={activeTab} items={tabItems} onChange={handleTabChange} />
      {scanResult && (
        <div style={{ marginTop: '20px', padding: '10px', backgroundColor: '#f0f0f0', borderRadius: '4px' }}>
          <TextSmall text={`QR Code ที่พบ: `} bold />
          <TextSmall text={scanResult} style={{ color: '#1890ff' }} />
        </div>
      )}
    </Modal>
  );
};
